import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AdProvider, Ad } from 'react-gpt-ads';

export const Advertisement = ({
    site = 'TEL.AFL',
    adType = 'PREMIUM',
    advertDimensions,
    adIndex,
    takeOver,
    enableLazyload,
    enableDebug
}) => {
    const META_KEYWORDS_KEY = 'keywords';
    const META_LONG_TITLE_KEY = 'longTitle';

    // Read DOM / globals ONCE and memoize them so they are stable
    const metaKeywordsProperty = useMemo(() => {
        return (
            document.querySelector(`[property="${META_KEYWORDS_KEY}"]`)
                ?.content || null
        );
    }, []);

    const metaLongTitleProperty = useMemo(() => {
        return (
            document.querySelector(`[property="${META_LONG_TITLE_KEY}"]`)
                ?.content || null
        );
    }, []);

    const isWebView = useMemo(() => {
        return (
            PULSE.core.url.getParam('webview') === 'true' ||
            window.IS_ARTICLE_API === true
        );
    }, []);

    const adTargetingGamblingValue = useMemo(
        () =>
            !!(
                PULSE &&
                PULSE.app &&
                PULSE.app.common &&
                PULSE.app.common.bettingVis &&
                PULSE.app.common.bettingVis.isVisible
            ),
        []
    );

    const over18 = useMemo(() => {
        try {
            return !!(
                PULSE &&
                PULSE.app &&
                PULSE.app.common &&
                PULSE.app.common.siteSettings &&
                PULSE.app.common.siteSettings.get().over18
            );
        } catch {
            return false;
        }
    }, []);

    const publisher = useMemo(() => {
        try {
            return PULSE.app.common.url.checkBaseRoute('aflw') ? 'aflw' : 'afl';
        } catch {
            return 'afl';
        }
    }, []);

    // Lock device and area once (area can be volatile if recomputed each render)
    const device = useMemo(() => {
        if (window.innerWidth > PULSE.app.measurements.tablet) {
            return 'desktop';
        }
        if (
            window.innerWidth < PULSE.app.measurements.tablet &&
            window.innerWidth > PULSE.app.measurements.phablet
        ) {
            return 'tablet';
        }
        return 'mobile';
    }, []);

    const _getSiteName = () => {
        if (!isWebView) {
            return site;
        }
        const configuredAndroidSite = PULSE?.adLib?.androidSite;
        const configuredIOSSite = PULSE?.adLib?.IOSSite;
        const isIOS = PULSE.app.common.deviceHelper.isIOSTouchScreen;
        const isAndroid = PULSE.app.common.deviceHelper.isAndroidTouchScreen;
        if (isIOS && configuredIOSSite) {
            return configuredIOSSite;
        }
        if (isAndroid && configuredAndroidSite) {
            return configuredAndroidSite;
        }
        return site;
    };

    const siteName = useMemo(() => _getSiteName(), [isWebView, site]);

    const area = useMemo(() => {
        // compute once and lock it
        if (isWebView) {
            return 'news/in_article';
        }
        const siteArea = document.querySelector(
            '[property="adverts:site-area"]'
        );
        if (siteArea) {
            return siteArea.getAttribute('content') || 'BLANK';
        }

        try {
            if (PULSE.app.common.url.contains('')) {
                return 'HOME';
            }
            if (PULSE.app.common.url.contains('news')) {
                return 'NEWS';
            }
            if (
                PULSE.app.common.url.contains('videos') ||
                PULSE.app.common.url.contains('video')
            ) {
                return 'VIDEOS';
            }
            if (PULSE.app.common.url.contains('ladder')) {
                return 'LADDER';
            }
            if (
                PULSE.app.common.url.contains('match-centre') ||
                PULSE.app.common.url.contains('match')
            ) {
                return 'MATCHES';
            }
            if (
                PULSE.app.common.url.contains('fixture', 2) ||
                PULSE.app.common.url.contains('fixtures', 2)
            ) {
                return 'FIXTURE';
            }
            if (
                PULSE.app.common.url.contains('players') ||
                PULSE.app.common.url.contains('teams') ||
                PULSE.app.common.url.contains('team')
            ) {
                return 'TEAMS';
            }
            if (PULSE.app.common.url.contains('injury-list', 2)) {
                return 'INJURY';
            }
        } catch (err) {
            // fallthrough
        }
        return 'BLANK';
    }, [isWebView]);

    // stable advert size — fix sorting and memoize
    const advertSize = useMemo(() => {
        if (!advertDimensions) {
            return '';
        }
        const dimensionsArray = advertDimensions
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        // sort by width descending (parseInt of width)
        const sorted = dimensionsArray.sort((a, b) => {
            const aw = parseInt(a.split('x')[0], 10) || 0;
            const bw = parseInt(b.split('x')[0], 10) || 0;
            return bw - aw;
        });
        const filtered = sorted.filter((d) => {
            const w = parseInt(d.split('x')[0], 10) || 0;
            return w < window.innerWidth;
        });
        return filtered.join(', ');
    }, [advertDimensions]);

    // beforeInit should use stable values (area, device, etc.)
    const beforeInit = useCallback(
        ({ targeting }) => {
            // small debug: you can comment out if too noisy
            // console.info('[Ad] beforeInit called with area/device:', area, device);

            targeting.setTargeting('device', device);
            targeting.setTargeting('section', area);

            if (!over18) {
                targeting.setTargeting('age', 'under18');
                targeting.exclude('Alcoholic_Beverages');
                targeting.exclude('Porn');
                targeting.exclude('Casino');
            } else {
                targeting.setTargeting('age', 'over18');
            }

            if (!over18 || !adTargetingGamblingValue) {
                targeting.setTargeting('gambling', 'false');
                targeting.exclude('Gambling');
            } else {
                targeting.setTargeting('gambling', 'true');
            }

            if (isWebView) {
                if (metaKeywordsProperty) {
                    targeting.setTargeting('keywords', metaKeywordsProperty);
                }
                if (metaLongTitleProperty) {
                    targeting.setTargeting('title', metaLongTitleProperty);
                }
            }
        },
        [
            area,
            device,
            over18,
            adTargetingGamblingValue,
            isWebView,
            metaKeywordsProperty,
            metaLongTitleProperty
        ]
    );

    // afterInit: prevent creating multiple intervals if called repeatedly
    const afterInit = useCallback(({ refresh, createAds }) => {
        // small debug
        // console.info('[Ad] afterInit called');

        // set references so window.refreshAds points to latest refresh function
        window.refreshAds = refresh;
        window.createDynamicAds = createAds;

        // Only create a single global interval (guard)
        if (!window.__adRefreshTimer) {
            window.__adRefreshTimer = setInterval(() => {
                try {
                    window.refreshAds && window.refreshAds();
                } catch (err) {
                    // swallow refresh errors
                    // console.error('refreshAds error', err);
                }
            }, 30000);
        }

        // NOTE: many libs won't call a cleanup returned from this callback,
        // so rely on the global guard above rather than returning cleanup here.
        // return () => clearInterval(window.__adRefreshTimer);
    }, []);

    // Small debug marker
    // console.info('-------- ADVERTISEMENT (render) ----------- ');

    return (
        <AdProvider
            site={siteName}
            area={area}
            publisher={publisher}
            debug={enableDebug === 'true'}
            beforeInit={beforeInit}
            afterInit={afterInit}
        >
            <Ad
                adType={adType}
                size={advertSize}
                adIndex={adIndex}
                lazyload={enableLazyload === 'true'}
                takeoverAd={takeOver === 'true'}
                disabled={false}
            />
        </AdProvider>
    );
};

Advertisement.propTypes = {
    site: PropTypes.string,
    area: PropTypes.string,
    adType: PropTypes.string,
    advertDimensions: PropTypes.string.isRequired,
    adIndex: PropTypes.number,
    enableLazyload: PropTypes.string,
    takeOver: PropTypes.string,
    hideBettingOdds: PropTypes.bool,
    under18: PropTypes.bool,
    noAlcohol: PropTypes.bool,
    noGambling: PropTypes.bool,
    enableDebug: PropTypes.string
};
